import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

// Configuration for static popup fallback
const STATIC_POPUP_CONFIG = {
  // imageUrl: "/assets/images/AGM.jpeg",
  altText: "Innovative Eco Krishi Promotion",
  linkUrl: "/investor-relations", // Page to navigate to if user clicks on the image
  delayMs: 1200, // Wait 1.2s before displaying the popup for smoother experience
  showOncePerSession: false, // Only show once per tab session
};

const PopupModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // Used for smooth transition/fade-in
  const [popupsList, setPopupsList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const location = useLocation();

  useEffect(() => {
    // Only load and trigger the popup on the homepage ('/')
    if (location.pathname !== '/') {
      setIsOpen(false);
      setIsVisible(false);
      return;
    }

    // Check if user has already seen the popup in this session (if configured)
    if (STATIC_POPUP_CONFIG.showOncePerSession) {
      const hasSeenPopup = sessionStorage.getItem('hasSeenPopupModal');
      if (hasSeenPopup) return;
    }

    const loadPopup = async () => {
      try {
        // Attempt to fetch popup messages from dynamic API endpoint
        const response = await fetch('/api/popupMessages');
        if (response.ok) {
          const data = await response.json();
          if (data.message === 'Popup messages fetched successfully' && data.popupMessages && data.popupMessages.length > 0) {
            // Find all active popups with image URLs
            const activePopups = data.popupMessages.filter(p => p.image_url);
            if (activePopups.length > 0) {
              setPopupsList(activePopups.map(p => ({
                imageUrl: p.image_url,
                altText: p.alt_text || p.title,
                linkUrl: null
              })));
              setCurrentIndex(0);
              triggerPopup();
              return;
            }
          }
        }
      } catch (error) {
        console.warn('Could not fetch dynamic popup message, falling back to static config:', error);
      }

      // Fallback: If API fetch fails/has no items, use static configuration (if imageUrl is defined)
      if (STATIC_POPUP_CONFIG.imageUrl) {
        setPopupsList([{
          imageUrl: STATIC_POPUP_CONFIG.imageUrl,
          altText: STATIC_POPUP_CONFIG.altText,
          linkUrl: STATIC_POPUP_CONFIG.linkUrl
        }]);
        setCurrentIndex(0);
        triggerPopup();
      }
    };

    const triggerPopup = () => {
      // Set timer to open popup after delayMs
      const timer = setTimeout(() => {
        setIsOpen(true);
        // Trigger visual fade-in slightly after opening state
        setTimeout(() => setIsVisible(true), 50);
        if (STATIC_POPUP_CONFIG.showOncePerSession) {
          sessionStorage.setItem('hasSeenPopupModal', 'true');
        }
      }, STATIC_POPUP_CONFIG.delayMs);

      return () => clearTimeout(timer);
    };

    loadPopup();
  }, [location.pathname]);

  // Handle Keyboard Navigation (Escape key to close/next)
  useEffect(() => {
    if (!isOpen || popupsList.length === 0) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, popupsList.length, currentIndex]);

  const handleClose = () => {
    setIsVisible(false);
    // Wait for transition animation to finish before showing the next popup or closing
    setTimeout(() => {
      if (currentIndex < popupsList.length - 1) {
        // Move to the next popup in the list
        setCurrentIndex((prev) => prev + 1);
        setIsVisible(true);
      } else {
        // Finished all popups, close modal
        setIsOpen(false);
      }
    }, 300);
  };

  if (!isOpen || popupsList.length === 0) return null;

  const currentPopup = popupsList[currentIndex];

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      onClick={handleClose}
      id="popup-overlay"
    >
      <div
        className={`relative w-auto h-auto max-w-[95vw] max-h-[90vh] sm:max-w-[80vw] rounded-2xl shadow-2xl transition-all duration-300 transform flex flex-col items-center justify-center ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
        id="popup-container"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-black/70 text-white rounded-full p-2 hover:bg-black hover:scale-110 active:scale-95 transition-all z-20 shadow-lg border border-white/20"
          aria-label="Close popup"
          id="popup-close-button"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Modal Image Area */}
        {currentPopup.linkUrl ? (
          <a href={currentPopup.linkUrl} className="block relative group flex items-center justify-center">
            <img
              src={currentPopup.imageUrl}
              alt={currentPopup.altText}
              className="w-auto h-auto max-w-[95vw] max-h-[90vh] sm:max-w-[80vw] object-contain rounded-2xl group-hover:scale-[1.01] transition-transform duration-500 ease-out bg-white"
              id="popup-image"
            />
            <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors duration-300 pointer-events-none rounded-2xl" />
          </a>
        ) : (
          <img
            src={currentPopup.imageUrl}
            alt={currentPopup.altText}
            className="w-auto h-auto max-w-[95vw] max-h-[90vh] sm:max-w-[80vw] object-contain rounded-2xl bg-white"
            id="popup-image"
          />
        )}
      </div>
    </div>
  );
};

export default PopupModal;
